import React from "react";
import ServicePageShell from "../ServicePageShell/ServicePageShell";
import { useIsDanish } from "../../i18n/LanguageContext";

const AutomationServices = () => {
  const isDa = useIsDanish();
  const automationAreas = [
    {
      title: "Infrastructure Automation (IaC)",
      desc: "Automated provisioning of Azure resources using Bicep and pipelines — consistent environments without manual clicks.",
      examples: [
        "Deploy VNets, subnets, NSGs, Private Endpoints",
        "Create App Services, Function Apps, Container Apps",
        "Environment-based deployments (dev / pre / prod)",
      ],
    },
    {
      title: "CI/CD & Release Automation",
      desc: "Build, test, validate, and deploy automatically with guardrails — faster releases with fewer incidents.",
      examples: [
        "Azure DevOps pipelines (validate, what-if, deploy)",
        "Approval gates & environment promotions",
        "Service connection & federated identity setup",
      ],
    },
    {
      title: "Identity, Access & PIM Automation",
      desc: "Automated user and access lifecycle with least privilege and just-in-time access.",
      examples: [
        "RBAC role assignments via code",
        "PIM group creation & role activation flows",
        "Onboarding / offboarding automation",
      ],
    },
    {
      title: "Operational Automation",
      desc: "Reduce manual operational work by automating repetitive admin and maintenance tasks.",
      examples: [
        "Log cleanup & retention enforcement",
        "Cost management & budget alerts",
        "Automated resource tagging & compliance fixes",
      ],
    },
    {
      title: "Integration & Workflow Automation",
      desc: "Connect systems and automate workflows across cloud services and business tools.",
      examples: [
        "Service Bus & Event-driven workflows",
        "Logic Apps & Azure Functions",
        "API-based system integrations",
      ],
    },
    {
      title: "Monitoring & Response Automation",
      desc: "Detect issues early and react automatically — before users notice.",
      examples: [
        "Alert-triggered remediation actions",
        "Auto-scaling rules",
        "Incident notifications & ticket creation",
      ],
    },
  ];

  const techStack = [
    "Azure DevOps",
    "Bicep & ARM",
    "Azure Functions",
    "Logic Apps",
    "Service Bus & Event Hubs",
    "Azure Monitor & Alerts",
    "Microsoft Entra ID",
    "PowerShell & Azure CLI",
  ];

  const whyAutomation = [
    {
      title: "Fewer errors",
      desc: "Automation removes human mistakes from repetitive tasks.",
    },
    {
      title: "Faster delivery",
      desc: "What used to take days can run in minutes.",
    },
    {
      title: "Lower costs",
      desc: "Less manual work and fewer incidents save money.",
    },
    {
      title: "Audit & compliance ready",
      desc: "Everything is logged, repeatable, and traceable.",
    },
  ];
  const automationAreasDa = [
    ["Infrastructure Automation (IaC)", "Automatiseret oprettelse af Azure-ressourcer med Bicep og pipelines, så miljøer bliver ens uden manuelle clicks.", ["Deploy VNets, subnets, NSGs og Private Endpoints", "Opret App Services, Function Apps og Container Apps", "Miljøbaserede deployments (dev / pre / prod)"]],
    ["CI/CD og release automation", "Build, test, valider og deploy automatisk med guardrails, så releases bliver hurtigere og mere sikre.", ["Azure DevOps pipelines", "Approval gates og miljøpromotion", "Service connections og federated identity"]],
    ["Identity, access og PIM automation", "Automatiseret bruger- og adgangslivscyklus med least privilege og just-in-time adgang.", ["RBAC assignments via kode", "PIM groups og role activation flows", "Onboarding/offboarding automation"]],
    ["Operationel automation", "Reducer manuelt driftsarbejde ved at automatisere gentagne admin- og vedligeholdelsesopgaver.", ["Log cleanup og retention", "Cost management og budget alerts", "Automatisk tagging og compliance fixes"]],
    ["Integration og workflow automation", "Forbind systemer og automatiser workflows på tværs af cloudservices og forretningsværktøjer.", ["Service Bus og event-driven workflows", "Logic Apps og Azure Functions", "API-baserede systemintegrationer"]],
    ["Monitoring og response automation", "Opdag problemer tidligt og reagér automatisk før brugerne mærker det.", ["Alert-triggered remediation", "Auto-scaling rules", "Incident notifications og tickets"]],
  ].map(([title, desc, examples]) => ({ title, desc, examples }));
  const whyAutomationDa = [
    { title: "Færre fejl", desc: "Automation fjerner menneskelige fejl fra gentagne opgaver." },
    { title: "Hurtigere levering", desc: "Det der før tog dage, kan ofte køre på minutter." },
    { title: "Lavere omkostninger", desc: "Mindre manuelt arbejde og færre incidents sparer penge." },
    { title: "Klar til audit og compliance", desc: "Alt er logget, gentageligt og sporbart." },
  ];
  const currentAutomationAreas = isDa ? automationAreasDa : automationAreas;
  const currentWhyAutomation = isDa ? whyAutomationDa : whyAutomation;

  return (
    <ServicePageShell>
      <div className="au-wrap">
        {/* HERO */}
        <section className="au-hero">
          <div className="au-heroInner">
            <div className="au-heroText">
              <div className="au-pill">
                <span className="au-dot" /> {isDa ? "Automatisering" : "Automation Services"}
              </div>

              <h1 className="au-title">
                {isDa ? "Automatisér det kedelige, risikable og gentagne." : "Automate the boring, the risky, and the repetitive."}
              </h1>

              <p className="au-subtitle">
                {isDa ? "Vi designer og implementerer automation, der sparer tid, reducerer fejl og lader jeres teams fokusere på rigtigt arbejde, ikke manuelle processer." : "We design and implement automation that saves time, reduces errors, and lets your teams focus on real work — not manual processes."}
              </p>

              <div className="au-ctaRow">
                <a className="au-btnPrimary" href="/#contact-section">
                  {isDa ? "Tal om automation" : "Talk automation"} <span aria-hidden="true">→</span>
                </a>
                <a className="au-btnGhost" href="/use-cases">
                  {isDa ? "Se cases" : "View use cases"}
                </a>
              </div>

              <div className="au-trust">
                <div className="au-trustItem">
                  <strong>{isDa ? "Stabil" : "Reliable"}</strong>
                  <span>{isDa ? "Gentagelig" : "Repeatable"}</span>
                </div>
                <div className="au-divider" />
                <div className="au-trustItem">
                  <strong>{isDa ? "Sikker" : "Secure"}</strong>
                  <span>Least privilege</span>
                </div>
                <div className="au-divider" />
                <div className="au-trustItem">
                  <strong>{isDa ? "Skalerbar" : "Scalable"}</strong>
                  <span>{isDa ? "Fra design" : "By design"}</span>
                </div>
              </div>
            </div>

            <div className="au-heroCard">
              <h3>{isDa ? "Typiske automationsresultater" : "Typical automation outcomes"}</h3>
              <ul className="au-list">
                <li>✔ {isDa ? "Miljøer deployet på minutter" : "Environments deployed in minutes"}</li>
                <li>✔ {isDa ? "Færre produktionsincidents" : "Fewer production incidents"}</li>
                <li>✔ {isDa ? "Hurtigere onboarding og access management" : "Faster onboarding & access management"}</li>
                <li>✔ {isDa ? "Tydelige audit trails" : "Clear audit trails"}</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="au-section">
          <div className="au-sectionHead">
            <h2>{isDa ? "Automation vi leverer" : "Automation we provide"}</h2>
            <p>
              {isDa ? "Fra infrastruktur og sikkerhed til drift og integrationer, bygget på de værktøjer der passer til jeres miljø." : "From infrastructure and security to operations and integrations — built on Microsoft technologies."}
            </p>
          </div>

          <div className="au-grid">
            {currentAutomationAreas.map((a) => (
              <div key={a.title} className="au-card">
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
                <ul>
                  {a.examples.map((e) => (
                    <li key={e}>→ {e}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* TECH STACK */}
        <section className="au-section au-stack">
          <h2>{isDa ? "Automation stack" : "Microsoft automation stack"}</h2>
          <div className="au-stackGrid">
            {techStack.map((t) => (
              <span key={t} className="au-chip">
                {t}
              </span>
            ))}
          </div>
        </section>

        {/* WHY */}
        <section className="au-section au-why">
          <h2>{isDa ? "Hvorfor automation betyder noget" : "Why automation matters"}</h2>
          <div className="au-whyGrid">
            {currentWhyAutomation.map((w) => (
              <div key={w.title} className="au-whyCard">
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="au-section au-final">
          <div className="au-finalCard">
            <h2>{isDa ? "Ikke sikker på hvad der bør automatiseres?" : "Not sure what to automate?"}</h2>
            <p>
              {isDa ? "Vi kan analysere jeres workflows og finde de steder, hvor automation giver størst effekt hurtigt." : "We can analyze your workflows and identify where automation gives the biggest return — fast."}
            </p>
            <a className="au-btnPrimary" href="/#contact-section">
              {isDa ? "Start dialogen" : "Start the conversation"} <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>
        <style>{`
  .au-wrap{
    max-width: 1100px;
    margin: 0 auto;
    padding: 28px 18px 70px;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Apple Color Emoji", "Segoe UI Emoji";
    color: #0f172a;
  }

  /* HERO */
  .au-hero{
    position: relative;
    border-radius: 28px;
    padding: 34px 20px;
    overflow: hidden;
    background:
      radial-gradient(1200px 500px at 10% 10%, rgba(59,130,246,0.18), transparent 60%),
      radial-gradient(900px 420px at 90% 10%, rgba(168,85,247,0.16), transparent 58%),
      linear-gradient(180deg, #ffffff, #f8fafc);
    border: 1px solid rgba(15,23,42,0.08);
    box-shadow:
      0 20px 60px rgba(2, 6, 23, 0.10),
      0 2px 12px rgba(2, 6, 23, 0.06);
  }

  .au-heroInner{
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 22px;
    align-items: center;
    position: relative;
    z-index: 2;
  }

  @media (max-width: 900px){
    .au-heroInner{ grid-template-columns: 1fr; }
  }

  .au-pill{
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 999px;
    background: rgba(2, 6, 23, 0.04);
    border: 1px solid rgba(15,23,42,0.08);
    font-weight: 800;
    font-size: 0.95rem;
    color: #0f172a;
  }

  .au-dot{
    width: 10px; height: 10px;
    border-radius: 999px;
    background: linear-gradient(135deg, #3b82f6, #a855f7);
    box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
  }

  .au-title{
    margin: 14px 0 10px;
    font-size: clamp(2rem, 2.6vw, 3.1rem);
    line-height: 1.06;
    letter-spacing: -0.02em;
  }

  .au-subtitle{
    margin: 0 0 18px;
    font-size: 1.08rem;
    line-height: 1.6;
    color: rgba(15, 23, 42, 0.78);
    max-width: 62ch;
  }

  .au-ctaRow{
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 18px;
  }

  .au-btnPrimary{
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 14px;
    text-decoration: none;
    font-weight: 900;
    color: white;
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
    border: 1px solid rgba(255,255,255,0.22);
    transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
  }
  .au-btnPrimary:hover{
    transform: translateY(-1px);
    filter: brightness(1.02);
    box-shadow: 0 18px 40px rgba(37, 99, 235, 0.22);
  }

  .au-btnGhost{
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 16px;
    border-radius: 14px;
    text-decoration: none;
    font-weight: 900;
    color: #0f172a;
    background: rgba(255,255,255,0.75);
    border: 1px solid rgba(15,23,42,0.10);
    backdrop-filter: blur(8px);
    transition: transform .15s ease, background .15s ease;
  }
  .au-btnGhost:hover{
    transform: translateY(-1px);
    background: rgba(255,255,255,0.95);
  }

  .au-trust{
    display: inline-flex;
    align-items: center;
    gap: 14px;
    padding: 12px 14px;
    border-radius: 18px;
    background: rgba(255,255,255,0.70);
    border: 1px solid rgba(15,23,42,0.08);
    backdrop-filter: blur(10px);
  }
  .au-trustItem{ display: grid; line-height: 1.1; }
  .au-trustItem strong{ font-size: 1rem; }
  .au-trustItem span{
    font-size: 0.9rem;
    color: rgba(15,23,42,0.7);
  }
  .au-divider{
    width: 1px;
    height: 28px;
    background: rgba(15,23,42,0.12);
  }

  /* HERO CARD */
  .au-heroCard{
    border-radius: 22px;
    background: rgba(255,255,255,0.70);
    border: 1px solid rgba(15,23,42,0.10);
    box-shadow: 0 18px 50px rgba(2, 6, 23, 0.10);
    padding: 16px;
    backdrop-filter: blur(10px);
  }
  .au-heroCard h3{
    margin: 0 0 10px;
    font-size: 1.1rem;
    letter-spacing: -0.01em;
  }
  .au-list{
    margin: 0;
    padding-left: 0;
    list-style: none;
    display: grid;
    gap: 10px;
    color: rgba(15,23,42,0.78);
    line-height: 1.5;
  }

  /* SECTIONS */
  .au-section{ margin-top: 34px; }

  .au-sectionHead{
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
  }
  .au-sectionHead h2{
    margin: 0;
    font-size: 1.7rem;
    letter-spacing: -0.02em;
  }
  .au-sectionHead p{
    margin: 0;
    color: rgba(15,23,42,0.72);
    max-width: 70ch;
    line-height: 1.55;
  }
  @media (max-width: 860px){
    .au-sectionHead{
      flex-direction: column;
      align-items: flex-start;
    }
  }

  /* GRID CARDS */
  .au-grid{
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }
  @media (max-width: 760px){
    .au-grid{ grid-template-columns: 1fr; }
  }

  .au-card{
    border-radius: 22px;
    background: white;
    border: 1px solid rgba(15,23,42,0.08);
    box-shadow: 0 12px 36px rgba(2,6,23,0.06);
    padding: 16px;
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .au-card:hover{
    transform: translateY(-2px);
    box-shadow: 0 18px 48px rgba(2,6,23,0.09);
  }
  .au-card h3{
    margin: 0 0 8px;
    font-size: 1.08rem;
    letter-spacing: -0.01em;
  }
  .au-card p{
    margin: 0 0 10px;
    color: rgba(15,23,42,0.72);
    line-height: 1.55;
  }
  .au-card ul{
    margin: 0;
    padding-left: 0;
    list-style: none;
    display: grid;
    gap: 8px;
    color: rgba(15,23,42,0.78);
  }
  .au-card li{
    padding: 10px 12px;
    border-radius: 16px;
    background: rgba(255,255,255,0.75);
    border: 1px solid rgba(15,23,42,0.08);
  }

  /* STACK */
  .au-stack{
    border-radius: 26px;
    padding: 22px;
    background:
      radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.12), transparent 55%),
      radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.10), transparent 55%),
      linear-gradient(180deg, #ffffff, #f8fafc);
    border: 1px solid rgba(15,23,42,0.08);
    box-shadow: 0 18px 50px rgba(2,6,23,0.07);
  }
  .au-stack h2{
    margin: 0 0 12px;
    font-size: 1.7rem;
    letter-spacing: -0.02em;
  }
  .au-stackGrid{
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .au-chip{
    display: inline-flex;
    align-items: center;
    padding: 10px 12px;
    border-radius: 999px;
    background: rgba(255,255,255,0.75);
    border: 1px solid rgba(15,23,42,0.10);
    font-weight: 800;
    color: rgba(15,23,42,0.85);
  }

  /* WHY */
  .au-why h2{
    margin: 0 0 12px;
    font-size: 1.7rem;
    letter-spacing: -0.02em;
  }
  .au-whyGrid{
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  @media (max-width: 760px){
    .au-whyGrid{ grid-template-columns: 1fr; }
  }
  .au-whyCard{
    border-radius: 18px;
    padding: 14px;
    background: white;
    border: 1px solid rgba(15,23,42,0.08);
    box-shadow: 0 10px 30px rgba(2,6,23,0.05);
  }
  .au-whyCard h3{
    margin: 0 0 6px;
    font-size: 1.02rem;
    letter-spacing: -0.01em;
  }
  .au-whyCard p{
    margin: 0;
    color: rgba(15,23,42,0.72);
    line-height: 1.55;
  }

  /* FINAL CTA */
  .au-finalCard{
    border-radius: 26px;
    padding: 22px;
    background:
      radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.10), transparent 55%),
      radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.09), transparent 55%),
      linear-gradient(180deg, #ffffff, #f8fafc);
    border: 1px solid rgba(15,23,42,0.08);
    box-shadow: 0 18px 50px rgba(2,6,23,0.07);
    text-align: left;
  }
  .au-finalCard h2{
    margin: 0 0 10px;
    font-size: 1.7rem;
    letter-spacing: -0.02em;
  }
  .au-finalCard p{
    margin: 0 0 14px;
    color: rgba(15,23,42,0.72);
    line-height: 1.6;
    max-width: 78ch;
  }
`}</style>
      </div>
    </ServicePageShell>
  );
};

export default AutomationServices;
